import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { Loader2 } from 'lucide-react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/utils/cn'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        /* Hover wash owned by `.btn-cta` (::before behind label — see index.css). */
        default: 'btn-cta text-primary-foreground active:scale-100',
        destructive:
          'bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90 active:scale-[0.98]',
        outline:
          'border border-border bg-card text-foreground shadow-xs hover:bg-secondary hover:border-border/80 active:scale-[0.98]',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80 active:scale-[0.98]',
        ghost: 'text-foreground hover:bg-secondary active:scale-[0.98]',
        link: 'text-primary underline-offset-4 hover:underline active:scale-100',
        success:
          'bg-success text-success-foreground shadow-xs hover:bg-success/90 active:scale-[0.98]',
      },
      size: {
        default: 'h-9 px-4',
        sm: 'h-8 rounded-md px-3 text-xs',
        xs: 'h-6 gap-1 rounded-md px-2 text-[10px] font-semibold leading-none',
        lg: 'h-10 rounded-lg px-5 text-sm',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    const isCta = !variant || variant === 'default'
    const content = loading ? (
      <>
        <Loader2 className="animate-spin" aria-hidden />
        <span className="sr-only">Loading</span>
        {children}
      </>
    ) : (
      children
    )

    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {/* CTA label wrap keeps icons/text above the ::before wash. asChild
         * cannot wrap (Slot = single child) — CSS keeps ::before at z-index -1
         * so bare Link text stays readable; prefer <span className="btn-cta-label">
         * inside asChild links when convenient. Never stack `btn-cta` on
         * variant="destructive" (ConfirmDialog / delete actions). */}
        {asChild || !isCta ? (
          content
        ) : (
          <span className="btn-cta-label inline-flex items-center justify-center gap-2">
            {content}
          </span>
        )}
      </Comp>
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
